<template>
  <div id="app">
    <!-- 全局加载状态 -->
    <transition name="fade" mode="out-in">
      <div v-if="isLoading" class="global-loading">
        <div class="loading-spinner"></div>
      </div>
    </transition>
    <!-- 路由视图 -->
    <router-view v-slot="{ Component, route }">
      <transition name="page" mode="out-in">
        <keep-alive :include="cachedComponents">
          <component :is="Component" :key="route.path" />
        </keep-alive>
      </transition>
    </router-view>
    <!-- 全局错误提示 -->
    <div v-if="globalError" class="global-error">
      <p>{{ globalError }}</p>
      <button @click="clearError">关闭</button>
    </div>
  </div>
</template>

<script>
import channelMaterial from '@/utils/channelMaterial'

export default {
  name: 'App',

  data() {
    return {
      isLoading: false,
      globalError: null,
      // 需要缓存的组件列表
      cachedComponents: ['Home']
    }
  },

  created() {
    this.initializeApp()
  },

  mounted() {
    this.setupGlobalHandlers()
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    /**
     * 初始化应用
     */
    initializeApp() {
      // 根据渠道设置页面title和favicon
      channelMaterial.setPageMeta()

      // 设置全局配置
      this.setupGlobalConfig()

      // 监听路由变化
      this.$router.beforeEach((to, from, next) => {
        this.isLoading = true
        next()
      })

      this.$router.afterEach(() => {
        this.isLoading = false
      })
    },

    /**
     * 设置全局配置
     */
    setupGlobalConfig() {
      // 设置全局错误处理
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
      window.addEventListener('error', this.handleGlobalError)
    },

    /**
     * 设置全局事件处理器
     */
    setupGlobalHandlers() {
      // 监听网络状态
      window.addEventListener('online', this.handleOnline)
      window.addEventListener('offline', this.handleOffline)
    },

    /**
     * 处理未捕获的 Promise 错误
     */
    handleUnhandledRejection(event) {
      this.showGlobalError('网络请求失败，请稍后重试')
      event.preventDefault()
    },

    /**
     * 处理全局错误
     */
    handleGlobalError(event) {
      console.error('全局错误:', event.error)
      this.showGlobalError('应用出现错误，请刷新页面重试')
    },

    /**
     * 显示全局错误信息
     */
    showGlobalError(message) {
      this.globalError = message
      // 5秒后自动清除错误信息
      setTimeout(() => {
        this.clearError()
      }, 5000)
    },

    /**
     * 清除错误信息
     */
    clearError() {
      this.globalError = null
    },

    /**
     * 网络连接恢复
     */
    handleOnline() {
      this.$message?.success('网络连接已恢复')
    },

    /**
     * 网络连接断开
     */
    handleOffline() {
      this.$message?.warning('网络连接已断开')
    },

    /**
     * 清理资源
     */
    cleanup() {
      window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)
      window.removeEventListener('error', this.handleGlobalError)
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
    },

    handleClose(done) {
      done()
    }
  }
}
</script>

<style>
/* ===== CSS 自定义属性（CSS Variables）===== */
:root {
  /* 主色调 */
  --primary-color: #409eff;
  --success-color: #67c23a;
  --warning-color: #e6a23c;
  --danger-color: #f56c6c;
  --info-color: #909399;

  /* 文本颜色 */
  --text-primary: #303133;
  --text-regular: #606266;
  --text-secondary: #909399;
  --text-placeholder: #c0c4cc;

  /* 边框颜色 */
  --border-base: #dcdfe6;
  --border-light: #e4e7ed;
  --border-lighter: #ebeef5;
  --border-extra-light: #f2f6fc;

  /* 背景颜色 */
  --bg-color: #ffffff;
  --bg-color-page: #f2f3f5;
  --bg-color-light: #fcfcfc;

  /* 字体 */
  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  --font-family-mono: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;

  /* 阴影 */
  --box-shadow-base: 0 2px 4px rgba(0, 0, 0, 0.12), 0 0 6px rgba(0, 0, 0, 0.04);
  --box-shadow-light: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  /* 过渡动画 */
  --transition-base: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  --transition-fast: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

/* ===== 现代化 CSS Reset ===== */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  line-height: 1.15;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
}

body {
  margin: 0;
  font: 12px / 20px "微软雅黑", "Microsoft Yahei", "宋体", "Arial";
  color: var(--text-primary);
  background-color: var(--bg-color-page);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* ===== 应用根容器 ===== */
#app {
  width: 100%;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* ===== 全局加载状态 ===== */
.global-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  /* z-index: var(--z-index-popper); */
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-lighter);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* ===== 全局错误提示 ===== */
.global-error {
  position: fixed;
  top: 20px;
  right: 20px;
  background: var(--danger-color);
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: var(--box-shadow-light);
  z-index: var(--z-index-popper);
  max-width: 400px;
}

.global-error button {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 12px;
  transition: var(--transition-fast);
}

.global-error button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* ===== 页面过渡动画 ===== */
.page-enter-active,
.page-leave-active {
  transition: var(--transition-base);
}

.page-enter-from {
  opacity: 0;
}

.page-title {
  font-size: 15px;
}

.page-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===== Element UI 组件样式优化 ===== */
.el-container,
.el-main,
.el-aside,
.el-header {
  box-shadow: none;
  border: none;
}

.el-menu {
  border-right: none;
}

.el-menu--horizontal {
  border-bottom: none;
}

/* ===== 滚动条样式 ===== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg-color-light);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--border-base);
  border-radius: 4px;
  transition: var(--transition-fast);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}

/* ===== 响应式断点 ===== */
@media (max-width: 768px) {
  body {
    font-size: 32px;
  }

  .global-error {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
}

.el-select-dropdown {
  z-index: 9999 !important;
}

.el-date-picker {
  z-index: 9999 !important;
}

.el-cascader__dropdown,
.el-popper,
.el-cascader-menus {
  z-index: 9999 !important;
}

.el-message-box__wrapper {
  z-index: 99999 !important;
}

/* 全局消息提示与通知层级，避免被弹窗（el-dialog）压住 */
.el-message {
  z-index: 99999 !important;
}

.el-notification {
  z-index: 99999 !important;
}
</style>
